/**
 * NowLineHeader Component
 * =======================
 * 
 * Implements the "Now-Line" concept from DESIGN_SPEC_CASE_PIPELINE_V2.md
 * Shows two summary cards at the top of any dashboard:
 * - LEFT: Needs Your Attention (you are the bottleneck)
 * - RIGHT: No Action Needed (AI handling / waiting on others)
 * 
 * This helps staff instantly know which cases need THEM vs. which are being handled.
 */

import React from 'react';
import { AlertTriangle, CheckCircle, Clock, ArrowRight } from 'lucide-react';

export interface NowLineStats {
  needsAttention: {
    count: number;
    breakdown: {
      label: string;
      count: number;
    }[];
  };
  noActionNeeded: {
    count: number;
    breakdown: {
      label: string;
      count: number;
    }[];
  };
}

interface NowLineHeaderProps {
  stats: NowLineStats;
  userName?: string;
  onViewNeedsAttention?: () => void;
  onViewNoAction?: () => void;
  loading?: boolean;
}

export default function NowLineHeader({
  stats,
  userName,
  onViewNeedsAttention,
  onViewNoAction,
  loading = false
}: NowLineHeaderProps) {
  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
          <div className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {/* Greeting Row */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">
          {greeting()}{userName ? `, ${userName}` : ''}
        </h1>
        <span className="text-sm text-gray-500">{formatDate()}</span>
      </div>

      {/* Now-Line Split Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT: Needs Your Attention */}
        <button
          onClick={onViewNeedsAttention}
          className="group bg-white border-2 border-amber-200 hover:border-amber-400 rounded-xl p-5 text-left transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-amber-800 uppercase tracking-wide">
                  Needs Your Attention
                </h3>
                <p className="text-3xl font-bold text-amber-900 mt-1">
                  {stats.needsAttention.count} {stats.needsAttention.count === 1 ? 'case' : 'cases'}
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-amber-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
          </div>
          
          {/* Breakdown */}
          {stats.needsAttention.breakdown.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-100">
              <div className="flex flex-wrap gap-2">
                {stats.needsAttention.breakdown.map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700"
                  >
                    {item.count} {item.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </button>

        {/* RIGHT: No Action Needed */}
        <button
          onClick={onViewNoAction}
          className="group bg-white border-2 border-green-200 hover:border-green-400 rounded-xl p-5 text-left transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-green-800 uppercase tracking-wide">
                  No Action Needed
                </h3>
                <p className="text-3xl font-bold text-green-900 mt-1">
                  {stats.noActionNeeded.count} {stats.noActionNeeded.count === 1 ? 'case' : 'cases'}
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-green-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
          </div>
          
          {/* Breakdown */}
          {stats.noActionNeeded.breakdown.length > 0 && (
            <div className="mt-3 pt-3 border-t border-green-100">
              <div className="flex flex-wrap gap-2">
                {stats.noActionNeeded.breakdown.map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700"
                  >
                    {item.count} {item.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}


/**
 * Helper to calculate NowLine stats from various data sources
 */
export function calculateNowLineStats(
  drafts: any[],
  reports: any[],
  role: 'data_specialist' | 'accountant' | 'attorney'
): NowLineStats {
  const needsAttention: NowLineStats['needsAttention'] = {
    count: 0,
    breakdown: []
  };
  const noActionNeeded: NowLineStats['noActionNeeded'] = {
    count: 0,
    breakdown: []
  };

  // Count breakdowns
  const attentionCounts: Record<string, number> = {};
  const noActionCounts: Record<string, number> = {};

  const allCases = [...drafts, ...reports];

  for (const caseData of allCases) {
    const status = caseData.status || caseData.assigned_queue || 'unknown';
    const transactionsNeedReview = caseData.transactions_need_review || 0;
    const isStale = caseData.stale || (caseData.updated_at && 
      (Date.now() - new Date(caseData.updated_at).getTime()) > 7 * 24 * 60 * 60 * 1000);
    const hasAnomalies = caseData.anomalies_count > 0;
    const isReadyForSignoff = caseData.is_ready_for_review || caseData.signed_off;
    const isComplete = status === 'complete' || status === 'completed';

    // Logic varies by role
    if (role === 'data_specialist') {
      // Data Specialist: needs attention if transactions need review or stale
      if (transactionsNeedReview > 0) {
        needsAttention.count++;
        attentionCounts['need review'] = (attentionCounts['need review'] || 0) + 1;
      } else if (isStale) {
        needsAttention.count++;
        attentionCounts['stale'] = (attentionCounts['stale'] || 0) + 1;
      } else {
        noActionNeeded.count++;
        if (status === 'processing') {
          noActionCounts['processing'] = (noActionCounts['processing'] || 0) + 1;
        } else if (status === 'waiting_client') {
          noActionCounts['waiting on client'] = (noActionCounts['waiting on client'] || 0) + 1;
        } else if (isReadyForSignoff) {
          noActionCounts['ready for accountant'] = (noActionCounts['ready for accountant'] || 0) + 1;
        } else {
          noActionCounts['other'] = (noActionCounts['other'] || 0) + 1;
        }
      }
    } else if (role === 'accountant') {
      // Accountant: needs attention if ready for signoff or has anomalies
      if (isReadyForSignoff && !isComplete) {
        needsAttention.count++;
        attentionCounts['ready for sign-off'] = (attentionCounts['ready for sign-off'] || 0) + 1;
      } else if (hasAnomalies) {
        needsAttention.count++;
        attentionCounts['has anomalies'] = (attentionCounts['has anomalies'] || 0) + 1;
      } else {
        noActionNeeded.count++;
        if (isComplete) {
          noActionCounts['completed'] = (noActionCounts['completed'] || 0) + 1;
        } else {
          noActionCounts['in progress'] = (noActionCounts['in progress'] || 0) + 1;
        }
      }
    } else if (role === 'attorney') {
      // Attorney: needs attention if released to them
      if (status === 'released' || status === 'attorney') {
        needsAttention.count++;
        attentionCounts['ready for you'] = (attentionCounts['ready for you'] || 0) + 1;
      } else {
        noActionNeeded.count++;
        noActionCounts['in progress'] = (noActionCounts['in progress'] || 0) + 1;
      }
    }
  }

  // Convert counts to breakdown arrays
  needsAttention.breakdown = Object.entries(attentionCounts).map(([label, count]) => ({ label, count }));
  noActionNeeded.breakdown = Object.entries(noActionCounts).map(([label, count]) => ({ label, count }));

  return { needsAttention, noActionNeeded };
}
